import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useSEO } from "../lib/seo";
import { SoftwareCard } from "../components/SoftwareCard";
import { Filter } from "lucide-react";

export default function SoftwareList() {
  const [software, setSoftware] = useState([]);
  const [features, setFeatures] = useState([]);
  const [selected, setSelected] = useState([]);
  const [params] = useSearchParams();
  const q = params.get("q") || "";

  useSEO({
    title: "Pool Service Software Reviews",
    description: "In-depth, independent reviews of every major pool service software platform: Skimmer, Pool Brain, Jobber, and more. Filter by the features you need.",
    canonicalPath: "/software",
    jsonLd: software.length
      ? {
          "@context": "https://schema.org",
          "@type": "ItemList",
          name: "Pool Service Software Reviews",
          itemListElement: software.map((s, i) => ({ "@type": "ListItem", position: i + 1, name: s.name, url: `${window.location.origin}/software/${s.slug}` })),
        }
      : undefined,
  });

  useEffect(() => {
    api.get("/features").then(({ data }) => setFeatures(data));
  }, []);

  useEffect(() => {
    const query = {};
    if (q) query.q = q;
    if (selected.length) query.feature = selected.join(",");
    api.get("/software", { params: query }).then(({ data }) => setSoftware(data));
  }, [q, selected]);

  const toggle = (f) =>
    setSelected((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold text-sky-600 mb-2">Reviews</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Pool Service Software Reviews</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
        Every platform researched and compared. Filter by the features your operation actually needs.
      </p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-4 gap-10">
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-2xl border border-border p-6 sticky top-24">
            <h2 className="font-heading font-bold text-slate-900 text-sm flex items-center gap-2 mb-4">
              <Filter className="w-4 h-4 text-sky-500" aria-hidden="true" /> Filter by feature
            </h2>
            <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
              {features.map((f) => (
                <label key={f} className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer hover:text-sky-600">
                  <input
                    type="checkbox"
                    data-testid={`feature-filter-${f.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    checked={selected.includes(f)}
                    onChange={() => toggle(f)}
                    className="rounded border-slate-300 text-sky-500 focus:ring-sky-400"
                  />
                  {f}
                </label>
              ))}
            </div>
            {selected.length > 0 && (
              <button data-testid="clear-filters-button" onClick={() => setSelected([])} className="mt-4 text-xs font-semibold text-sky-600 hover:text-sky-700">
                Clear filters
              </button>
            )}
          </div>
        </aside>
        <div className="lg:col-span-3 space-y-5">
          {q && <p className="text-sm text-slate-500">Showing results for "<span className="font-semibold text-slate-900">{q}</span>"</p>}
          {software.map((s) => (
            <SoftwareCard key={s.slug} software={s} />
          ))}
          {software.length === 0 && (
            <div className="text-center py-20 text-slate-400" data-testid="no-results-message">No software matches those filters yet.</div>
          )}
        </div>
      </div>
    </div>
  );
}
